// src/components/general/BaseList.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
    Table,
    Input,
    App,
    Button,
    Card,
    Typography,
    Select,
    Popconfirm,
    Pagination,
    Skeleton,
    Row,
    Col,
    Empty,
    Space,
} from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import BaseExcelUploadModal from "./BaseExcelUploadModal";
import API from "API";
import { exportToExcel } from "general_function";

const { Search } = Input;
const { Paragraph } = Typography;
const { Option } = Select;

const DEFAULT_CARD_PAGE_SIZE = 9;
const DEFAULT_LIST_PAGE_SIZE = 10;

const CARD_PAGE_SIZE_LIST = [9, 27, 45, 81];
const LIST_PAGE_SIZE_LIST = [10, 20, 50, 100];

export const defaultRenderItem = (
    item,
    defaultLogo,
    { onEdit, onDelete, onClick, customActions, renderContentCard } = {}
) => (
    <Card
        className="card-equal-height"
        hoverable
        onClick={() => {
            onClick?.(item);
        }}
        style={{ transition: "transform 0.3s" }}
        onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-5px)";
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.transform = "none";
        }}
    >
        <div style={{ display: "flex", gap: 16, height: "100%" }}>
            <div>
                {item.logo || defaultLogo ? (
                    <img
                        alt={item.name}
                        src={item.logo || defaultLogo}
                        style={{
                            width: 120,
                            height: 120,
                            objectFit: "cover",
                            borderRadius: "50%",
                        }}
                    />
                ) : (
                    <div
                        style={{
                            width: 120,
                            height: 120,
                            backgroundColor: "#f0f0f0",
                            borderRadius: "50%",
                        }}
                    />
                )}
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                {renderContentCard ? (
                    <>{renderContentCard(item)}</>
                ) : (
                    <>
                        <Typography.Title level={5}>
                            {item.formatName || item.name}
                        </Typography.Title>
                        <Paragraph className="card-list-description">
                            <div dangerouslySetInnerHTML={{__html: item.formatDescription || item.description || "Không có mô tả."}} />
                        </Paragraph>
                    </>
                )}
                <div style={{ marginTop: "auto", textAlign: "center" }}>
                    {customActions ? (
                        customActions(item)
                    ) : (
                        <>
                            <Button
                                type="link"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit?.(item);
                                }}
                            >
                                Sửa
                            </Button>
                            <Popconfirm
                                title="Bạn có chắc muốn xóa mục này?"
                                onConfirm={(e) => {
                                    e?.stopPropagation?.();
                                    onDelete?.(item);
                                }}
                                okText="Xóa"
                                cancelText="Hủy"
                                onCancel={(e) => e?.stopPropagation?.()}
                            >
                                <Button
                                    type="link"
                                    danger
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Xóa
                                </Button>
                            </Popconfirm>
                        </>
                    )}
                </div>
            </div>
        </div>
    </Card>
);

const BaseList = ({
    columns,
    fetchData,
    actions,
    renderItem,
    listMode = "table",
    itemName = "mục",
    reloadKey = 0,
    useFrontendPagination = false,
    disableSearch = false,
    onCardClick = null,
    defaultLogo = "",
    onEdit,
    onDelete,
    customActions,
    excelImportExportOptions = null,
    onTableClick = null,
    expandable=null,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchValue, setSearchValue] = useState(
        searchParams.get("search") || ""
    );
    const [totalItems, setTotalItems] = useState(0);
    const [fullData, setFullData] = useState([]);
    const [excelModalVisible, setExcelModalVisible] = useState(false);

    const { message } = App.useApp();

    const currentPage = parseInt(searchParams.get("page")) || 1;
    const pageSize =
        parseInt(searchParams.get("pageSize")) ||
        (listMode === "table"
            ? DEFAULT_LIST_PAGE_SIZE
            : DEFAULT_CARD_PAGE_SIZE);
    const searchTerm = searchParams.get("search") || "";

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchData({
                searchTerm,
                page: currentPage,
                pageSize,
            });
            const items = Array.isArray(response.data) ? response.data : [];
            if (!useFrontendPagination) {
                setData(items);
                setTotalItems(response.total || 0);
            } else {
                setFullData(items);
                setData(
                    items.slice(
                        (currentPage - 1) * pageSize,
                        currentPage * pageSize
                    )
                );
            }
        } catch (error) {
            console.error("Lỗi loadData:", error);
            message.error(`Lỗi khi tải danh sách ${itemName}.`);
        }
        setLoading(false);
    }, [
        fetchData,
        searchTerm,
        currentPage,
        pageSize,
        useFrontendPagination,
        itemName,
        message,
    ]);

    useEffect(() => {
        loadData();
    }, [loadData, reloadKey, searchTerm, currentPage, pageSize]);

    const handleSearch = (value) => {
        setSearchValue(value);
        setSearchParams({ page: 1, pageSize, search: value });
    };
    const handleClearFilters = () => {
        setSearchValue("");
        setSearchParams({ page: 1, pageSize });
    };
    const handlePageChange = (page) => {
        setSearchParams({ page, pageSize, search: searchValue });
    };
    const handlePageSizeChange = (value) => {
        setSearchParams({ page: 1, pageSize: value, search: searchValue });
    };

    const handleExportExcel = async () => {
        try {
            const res = await API.get(excelImportExportOptions.apiPath, {
                params: { page_size: 1000 },
            });
            exportToExcel(
                res.data?.results || [],
                `${excelImportExportOptions.expectedCsvType}_list`,
                excelImportExportOptions.exportFields
            );
        } catch (error) {
            console.error("Lỗi khi export:", error);
        }
    };

    const alignedColumns = (columns || []).map((col) =>
        listMode === "table" ? { ...col, align: "center" } : col
    );
    const enhancedColumns =
        listMode === "table" && (onEdit || onDelete || customActions)
            ? [
                  ...alignedColumns,
                  {
                      title: "Hành động",
                      key: "actions",
                      align: "center",
                      render: (_, record) => (
                          <Space size={24}>
                              {customActions ? (
                                  customActions(record)
                              ) : (
                                  <>
                                      {onEdit && (
                                          <Button
                                              icon={<EditOutlined />}
                                              type="text"
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  onEdit(record);
                                              }}
                                              title="Sửa"
                                          />
                                      )}
                                      {onDelete && (
                                          <Popconfirm
                                              title="Bạn có chắc muốn xóa mục này?"
                                              onConfirm={(e) => {
                                                  e.stopPropagation();
                                                  onDelete(record);
                                              }}
                                              okText="Xóa"
                                              cancelText="Hủy"
                                          >
                                              <Button
                                                  icon={<DeleteOutlined />}
                                                  type="text"
                                                  danger
                                                  onClick={(e) => {
                                                      e.stopPropagation();
                                                  }}
                                                  title="Xóa"
                                              />
                                          </Popconfirm>
                                      )}
                                  </>
                              )}
                          </Space>
                      ),
                  },
              ]
            : alignedColumns;

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Space wrap size={[8, 8]}>
                    {actions}
                    {excelImportExportOptions && (
                        <>
                            <Button
                                type="primary"
                                onClick={() => setExcelModalVisible(true)}
                            >
                                Nhập từ Excel
                            </Button>
                            <Button type="primary" onClick={handleExportExcel}>
                                Tải danh sách
                            </Button>
                        </>
                    )}
                </Space>
            </div>

            <div
                style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginBottom: 16,
                }}
            >
                {!disableSearch && (
                    <>
                        <Search
                            placeholder={`Tìm kiếm ${itemName}...`}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onSearch={handleSearch}
                            style={{ width: 250 }}
                            loading={loading}
                            disabled={loading}
                        />
                        {searchTerm && (
                            <Button
                                onClick={handleClearFilters}
                                type="default"
                                disabled={loading}
                            >
                                Xóa bộ lọc
                            </Button>
                        )}
                    </>
                )}
                <div style={{ marginLeft: "auto" }}>
                    <Select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        disabled={loading}
                        style={{ minWidth: 120 }}
                    >
                        {(listMode === "table"
                            ? LIST_PAGE_SIZE_LIST
                            : CARD_PAGE_SIZE_LIST
                        ).map((num) => (
                            <Option key={num} value={num}>
                                {num} / trang
                            </Option>
                        ))}
                    </Select>
                </div>
            </div>

            {listMode === "table" ? (
                <div style={{ overflowX: "auto" }}>
                    <Table
                        columns={enhancedColumns}
                        dataSource={data}
                        rowKey="id"
                        loading={loading}
                        scroll={{ x: "max-content" }} // vẫn giữ
                        locale={{
                            emptyText: (
                                <Empty description={`Không có ${itemName}`} />
                            ),
                        }}
                        rowClassName={onTableClick ? "cursor-pointer" : ""}
                        onRow={(record, rowIndex) => {
                            return {
                                onClick: (e) => {
                                    e.stopPropagation();
                                    onTableClick?.(record);
                                },
                            };
                        }}
                        pagination={{
                            current: currentPage,
                            pageSize,
                            total: useFrontendPagination
                                ? fullData.length
                                : totalItems,
                            onChange: handlePageChange,
                            showSizeChanger: false,
                        }}
                        expandable={expandable}
                    />
                </div>
            ) : loading ? (
                <Skeleton active />
            ) : data.length === 0 ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: 40,
                    }}
                >
                    <Empty description={`Không có ${itemName}`} />
                </div>
            ) : (
                <>
                    <Row gutter={[16, 16]}>
                        {data.map((item) => (
                            <Col
                                key={item.id}
                                xs={24}
                                sm={12}
                                md={8}
                                lg={8}
                                xl={8}
                                style={{ display: "flex" }}
                            >
                                <div style={{ width: "100%" }}>
                                    {(renderItem || defaultRenderItem)(
                                        item,
                                        defaultLogo,
                                        {
                                            onEdit,
                                            onDelete,
                                            onClick: onCardClick,
                                            customActions,
                                        }
                                    )}
                                </div>
                            </Col>
                        ))}
                    </Row>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: 16,
                        }}
                    >
                        <Pagination
                            current={currentPage}
                            pageSize={pageSize}
                            total={
                                useFrontendPagination
                                    ? fullData.length
                                    : totalItems
                            }
                            onChange={handlePageChange}
                        />
                    </div>
                </>
            )}

            {excelImportExportOptions && (
                <BaseExcelUploadModal
                    visible={excelModalVisible}
                    onClose={() => setExcelModalVisible(false)}
                    onImportSuccess={() => {
                        setExcelModalVisible(false);
                        loadData();
                    }}
                    onReloadList={loadData}
                    apiPath={excelImportExportOptions.apiPath}
                    itemName={itemName}
                    expectedCsvType={excelImportExportOptions.expectedCsvType}
                    resetOnClose
                />
            )}
        </div>
    );
};

export default BaseList;
